/**
 * Chart extents utilities for including order prices in chart Y-axis calculations
 * Ensures all orders (main and OTO) are visible within the chart range
 */

import { router } from "../data/Services/ordersRouter.jsx";

/**
 * Extracts minimum and maximum prices from orders (including OTO orders)
 * @param {Array} orders - Array of order objects
 * @returns {Object} { min, max } prices from all orders
 */
/**
 * Extracts position entry price as extent
 * @param {Object} position - Position object with price property
 * @returns {Object} { min, max } prices from position (same value for both)
 */
export const getPositionExtents = (position) => {
  if (
    !position ||
    !position.price ||
    !isFinite(position.price) ||
    position.price <= 0
  ) {
    return { min: null, max: null };
  }
  // Position entry price becomes both min and max (single price point)
  return { min: position.price, max: position.price };
};

export const getOrdersExtents = (orders) => {
  if (!orders || !Array.isArray(orders) || orders.length === 0) {
    return { min: null, max: null };
  }

  let min = Infinity;
  let max = -Infinity;

  orders.forEach((order) => {
    // Include main order price - ensure it's a valid positive number
    if (order.price && isFinite(order.price) && order.price > 0) {
      min = Math.min(min, order.price);
      max = Math.max(max, order.price);
    }

    // Include OTO order prices (stops and profits) - ensure they're valid positive numbers
    if (order.oto && Array.isArray(order.oto)) {
      order.oto.forEach((otoOrder) => {
        if (otoOrder.price && isFinite(otoOrder.price) && otoOrder.price > 0) {
          min = Math.min(min, otoOrder.price);
          max = Math.max(max, otoOrder.price);
        }
      });
    }
  });

  // Return null values if no valid prices found
  if (!isFinite(min) || !isFinite(max)) {
    return { min: null, max: null };
  }

  return { min, max };
};

/**
 * Creates an enhanced yExtents function that includes order prices
 * @param {Function} originalYExtents - Original yExtents function from chart config
 * @param {Array} orders - Array of order objects for current symbol
 * @returns {Function} Enhanced yExtents function that includes order prices
 */
export const createEnhancedYExtents = (originalYExtents, orders) => {
  return (d, index, allData) => {
    // Get original extents from chart data
    let originalExtents;
    try {
      originalExtents = originalYExtents
        ? originalYExtents(d, index, allData)
        : null;
    } catch {
      originalExtents = null;
    }

    // If no original extents, return fallback
    if (
      !originalExtents ||
      !Array.isArray(originalExtents) ||
      originalExtents.length !== 2
    ) {
      return [1, 100];
    }

    let [min, max] = originalExtents;
    const originalMin = min;
    const originalMax = max;
    let expandedByOrders = false;

    // Include order extents if we have orders
    if (orders && orders.length > 0) {
      const orderExtents = getOrdersExtents(orders);

      if (orderExtents.min !== null && orderExtents.max !== null) {
        // Expand the range to include order prices
        min = Math.min(min, orderExtents.min);
        max = Math.max(max, orderExtents.max);
        expandedByOrders = min !== originalMin || max !== originalMax;
      }
    }

    // Ensure we have valid range
    if (!isFinite(min) || !isFinite(max)) {
      return originalExtents;
    }

    // Prevent min === max
    if (min === max) {
      const padding = Math.max(Math.abs(min) * 0.1, 10);
      return [min - padding, max + padding];
    }

    if (expandedByOrders) {
      // Add small padding for better visibility only when orders forced expansion
      const range = max - min;
      const padding = range * 0.02; // 2% padding
      return [min - padding, max + padding];
    }

    return [min, max];
  };
};

/**
 * Translates a chart symbol to the actual trading symbol (handles FUL/FUT
 * suffixes). Privada do módulo — o único consumidor externo (ChartRenderer)
 * migrou para usePositionData; aqui segue servindo filterOrdersBySymbol.
 * @param {string} symbol - Chart symbol (e.g., "WINFUL", "WINFUT", "WING26")
 * @returns {string} Translated symbol (e.g., "WINQ26")
 */
const translateChartSymbol = (symbol) => {
  if (!symbol) return symbol;

  try {
    if (router && router.translateFutSymbol) {
      let translated = router.translateFutSymbol(symbol);

      // Handle FUL suffix (WINFUL, WDOFUL) - convert to FUT and translate
      if (
        translated === symbol &&
        (symbol.endsWith("FUL") || symbol.endsWith("FUT"))
      ) {
        const prefix = symbol.substring(0, 3); // WIN, WDO
        const futSymbol = prefix + "FUT";
        const futTranslated = router.translateFutSymbol(futSymbol);
        if (futTranslated !== futSymbol) {
          translated = futTranslated;
        }
      }

      return translated;
    }
  } catch {
    // Silent fallback
  }

  return symbol;
};

/**
 * Filters orders by symbol to only include relevant orders for the current chart
 * @param {Array} orders - All orders
 * @param {string} symbol - Current chart symbol
 * @returns {Array} Filtered orders for the symbol
 */
export const filterOrdersBySymbol = (orders, symbol) => {
  if (!orders || !symbol) {
    return [];
  }

  // Use translateChartSymbol which handles FUL/FUT suffixes properly
  const translatedSymbol = translateChartSymbol(symbol);

  const filtered = orders.filter((order) => {
    // Check main order symbol against both original and translated symbol
    if (order.symbol === symbol || order.symbol === translatedSymbol) {
      return true;
    }

    // Check OTO order symbols (they should match main order symbol)
    if (order.oto && Array.isArray(order.oto)) {
      const hasMatchingOTO = order.oto.some(
        (otoOrder) =>
          otoOrder.symbol === symbol || otoOrder.symbol === translatedSymbol,
      );
      if (hasMatchingOTO) {
        return true;
      }
    }

    return false;
  });

  return filtered;
};
